// Settings: the sections that are switched on (stations & devices, held and rejected
// station actions, staff and roles, stock control) and what comes later. Each section shows
// to those who may open it: stations with `devices.manage`, staff and roles with
// `staff.manage` (B-080).

import { Badge, Card, formatMoney, PageHeader, Skeleton, StatusBadge } from '@enp/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Ban,
  ChevronRight,
  ClipboardCheck,
  Inbox,
  type LucideIcon,
  ShieldCheck,
  TabletSmartphone,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useDocumentTitle } from '../../../shell/page.js';
import { stockControlSettingsQuery } from '../../../stock-control/queries.js';
import { signOutText } from '../../../stock-control/words.js';
import {
  NoDeviceAccess,
  SettingsFrame,
  useCanManageDevices,
} from '../../../stations/page-parts.js';
import { holdsQuery, rejectedQuery, stationsQuery } from '../../../stations/queries.js';
import { useCanManageStaff } from '../../../staff/components.js';
import { rolesQuery, staffQuery } from '../../../staff/queries.js';

export const Route = createFileRoute('/_app/settings/')({
  component: SettingsPage,
});

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function SectionLink({
  to,
  icon: Icon,
  title,
  description,
  summary,
}: {
  to:
    | '/settings/stations'
    | '/settings/held-actions'
    | '/settings/rejected-actions'
    | '/settings/stock-control'
    | '/settings/staff'
    | '/settings/roles';
  icon: LucideIcon;
  title: string;
  description: string;
  summary: ReactNode;
}) {
  return (
    <li>
      <Card className="relative flex-row items-center gap-4 p-5 transition-colors hover:bg-surface-hover">
        <Icon aria-hidden className="size-6 shrink-0 text-muted" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="font-display text-lg font-semibold text-fg">
            <Link to={to} className="after:absolute after:inset-0 after:rounded-lg">
              {title}
            </Link>
          </h2>
          <p className="text-body text-muted">{description}</p>
          <div className="text-sm text-fg">{summary}</div>
        </div>
        <ChevronRight aria-hidden className="size-5 shrink-0 text-muted" />
      </Card>
    </li>
  );
}

function SettingsPage() {
  useDocumentTitle('Settings');
  const canManage = useCanManageDevices();
  const stations = useQuery({ ...stationsQuery(), enabled: canManage === true });
  const holds = useQuery({ ...holdsQuery('open'), enabled: canManage === true });
  const rejected = useQuery({ ...rejectedQuery(), enabled: canManage === true });
  const stockControl = useQuery(stockControlSettingsQuery());
  const canStaff = useCanManageStaff();
  const staff = useQuery({ ...staffQuery(), enabled: canStaff === true });
  const roles = useQuery({ ...rolesQuery(), enabled: canStaff === true });
  const noPin = staff.data?.staff.filter((s) => !s.has_pin).length ?? 0;
  const staffSummary = staff.data ? (
    <span className="flex flex-wrap items-center gap-2">
      {plural(staff.data.staff.length, 'person', 'people')}
      {noPin > 0 && (
        <StatusBadge status="warning">
          {plural(noPin, 'without a PIN', 'without a PIN')}
        </StatusBadge>
      )}
    </span>
  ) : staff.isError ? (
    <span className="text-muted">Couldn’t load the count</span>
  ) : (
    <Skeleton className="h-4 w-32" />
  );
  const rolesSummary = roles.data ? (
    <span>{roles.data.roles.map((r) => r.name).join(' · ')}</span>
  ) : roles.isError ? (
    <span className="text-muted">Couldn’t load the roles</span>
  ) : (
    <Skeleton className="h-4 w-64" />
  );

  const stationSummary = stations.data ? (
    <span>
      {plural(stations.data.stations.length, 'station', 'stations')} ·{' '}
      {plural(
        stations.data.stations.flatMap((s) => s.devices.filter((d) => !d.revoked_at)).length,
        'active device',
        'active devices',
      )}
    </span>
  ) : stations.isError ? (
    <span className="text-muted">Couldn’t load the count</span>
  ) : (
    <Skeleton className="h-4 w-40" />
  );
  const open = holds.data?.open_count;
  const holdSummary =
    open === undefined ? (
      holds.isError ? (
        <span className="text-muted">Couldn’t load the count</span>
      ) : (
        <Skeleton className="h-4 w-32" />
      )
    ) : open > 0 ? (
      <StatusBadge status="warning">
        {plural(open, 'action waiting', 'actions waiting')}
      </StatusBadge>
    ) : (
      <Badge tone="neutral">Nothing waiting</Badge>
    );
  const refused = rejected.data;
  const rejectedSummary = refused ? (
    <span>
      {refused.items.length === 0
        ? 'Nothing refused'
        : `${plural(refused.items.length, 'action', 'actions')}${refused.truncated ? '+' : ''} refused`}
    </span>
  ) : rejected.isError ? (
    <span className="text-muted">Couldn’t load the count</span>
  ) : (
    <Skeleton className="h-4 w-32" />
  );

  // The same width as every Settings screen (UI3A-213).
  const sc = stockControl.data;
  const stockControlSummary = sc ? (
    <span>
      Write-offs from {formatMoney(sc.writeoff_min_cents)} · counts from {sc.count_adjust_pct}% or{' '}
      {formatMoney(sc.count_adjust_min_cents)} · list at {sc.state_list_time} · sign-out{' '}
      {signOutText(sc.station_auto_sign_out_minutes).toLowerCase()}
    </span>
  ) : stockControl.isError ? (
    <span className="text-muted">Couldn’t load the settings</span>
  ) : (
    <Skeleton className="h-4 w-64" />
  );

  return (
    <SettingsFrame>
      <PageHeader
        title="Settings"
        description="The stations that run the day and their devices, the people who sign in at them, station actions waiting for a manager, and the ones the server refused."
      />
      {canManage === false ? (
        <NoDeviceAccess />
      ) : (
        <ul aria-label="Settings sections" className="flex flex-col gap-3">
          <SectionLink
            to="/settings/stations"
            icon={TabletSmartphone}
            title="Stations & devices"
            description="Add stations, pair tablets and PCs to them with a one-time key, and revoke lost ones."
            summary={stationSummary}
          />
          <SectionLink
            to="/settings/held-actions"
            icon={Inbox}
            title="Held station actions"
            description="Actions a station sent that need a person before they count. Nothing in them is lost."
            summary={holdSummary}
          />
          <SectionLink
            to="/settings/rejected-actions"
            icon={Ban}
            title="Rejected station actions"
            description="Actions the server refused for good, such as one done without the permission. See who, when and why."
            summary={rejectedSummary}
          />
        </ul>
      )}
      {canStaff && (
        <ul aria-label="Staff settings" className="mt-3 flex flex-col gap-3">
          <SectionLink
            to="/settings/staff"
            icon={Users}
            title="Staff & PINs"
            description="The people who sign in at a station: add them, give them a role and a PIN, and unlock a locked station."
            summary={staffSummary}
          />
          <SectionLink
            to="/settings/roles"
            icon={ShieldCheck}
            title="Roles"
            description="What each role allows, in plain words. Change the defaults or add your own."
            summary={rolesSummary}
          />
        </ul>
      )}
      <ul aria-label="Stock control settings" className="mt-3 flex max-w-3xl flex-col gap-3">
        <SectionLink
          to="/settings/stock-control"
          icon={ClipboardCheck}
          title="Stock control"
          description="When counts and write-offs need a manager, when the daily expiry list is made, and when stations sign people out."
          summary={stockControlSummary}
        />
      </ul>
      <p className="mt-6 max-w-3xl text-sm text-muted">Business details come later.</p>
    </SettingsFrame>
  );
}
